import { addDays, differenceInDays, endOfMonth, startOfMonth, subDays } from 'date-fns'
import { action, comparer, computed, makeObservable, observable, override } from 'mobx'
import { matchPath } from 'react-router-dom'

import {
  AccountStatus,
  GuestReadinessStatus,
  HomeOperationsReadinessStatus,
  ListingStatus,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDate, parseISODate } from '@avantstay/backoffice-core'

import { fastTapeChartPaths } from '@new/__global/components/FastTapeChart/config/contants'
import { FilterStore } from '@new/__global/components/FiltersFramework'
import { AvailableDatesRange, BookingsColorMode } from '@new/domains/fastBookingsTapeChart/__types'
import {
  DEFAULT_VIEW_FROM_DATE,
  ISO_DATE_FORMAT,
  TIME_FRAME_DAYS,
} from '@new/domains/fastBookingsTapeChart/config/contants'
import {
  CustomFilterIds,
  fastTapeChartFiltersConfig,
  HomeFilterIds,
  tapechartGridSizingStore,
  ViewStateFilterIds,
} from '@new/domains/fastBookingsTapeChart/internal'

import { ROUTES } from '@routes'
import { computedRouteParam } from '@stores/computedRouteParam'
import { RoutingStore } from '@stores/RoutingStore'

export class FastBookingsTapeChartFiltersStore extends FilterStore {
  areInitialFiltersApplied = false

  constructor() {
    super(fastTapeChartFiltersConfig, [ROUTES.fastTapeChart.path])

    makeObservable(this, {
      areInitialFiltersApplied: observable,
      isTapeChartActive: computed,
      setInitialFiltersApplied: action.bound,
      viewFrom: computed({ equals: comparer.structural }),
      startDate: computed,
      endDate: computed,
      propertyId: computed,
      bookingId: computed,
      blockId: computed,
      regions: computed,
      nightlyRates: computed,
      reservationStatus: computed,
      paymentStatus: computed,
      depositOnHold: computed,
      bookingIssues: computed,
      colorMode: computed,
      operationReadinessStatus: computed,
      arrivalsMode: computed,
      checkInDates: computed,
      availableDates: computed,
      setViewFrom: action.bound,
      setColorMode: action.bound,
      setAddressFilter: action.bound,
      setOperationReadinessStatus: action.bound,
      setCheckInDates: action.bound,
      clearAllFilters: override,
      applyDefaultFilters: action.bound,
      toggleArrivalMode: action.bound,
      setBlockId: action.bound,
      setBookingId: action.bound,
    })
  }

  get viewFrom() {
    const viewFrom = this.computedRouteParams[ViewStateFilterIds.ViewFrom].get()

    return viewFrom ? parseISODate(viewFrom) : DEFAULT_VIEW_FROM_DATE
  }

  get startDate() {
    // we can't start our calendar from last day of month
    // because header will not fit and break the grid
    // we also can't use overflow: hidden as it break position: sticky
    // that's why in such case we add one more day so the header will have enough space
    return differenceInDays(this.viewFrom, endOfMonth(this.viewFrom)) === 0
      ? subDays(this.viewFrom, 1)
      : this.viewFrom
  }

  get endDate() {
    // we can't end our calendar on the last first of month
    // because header will not fit and grid will have empty space at the end
    // we also can't use overflow: hidden as it break position: sticky
    // that's why in such case we add one more day so the header will have enough space
    const endDate = addDays(this.viewFrom, TIME_FRAME_DAYS)
    return differenceInDays(endDate, startOfMonth(endDate)) === 0 ? addDays(endDate, 1) : endDate
  }

  private propertyIdParam = computedRouteParam<string | undefined>('property-id', {
    patterns: fastTapeChartPaths,
  })

  private bookingIdParam = computedRouteParam<string | undefined>('booking-id', {
    patterns: fastTapeChartPaths,
  })

  private blockIdParam = computedRouteParam<string | undefined>('block-id', {
    patterns: fastTapeChartPaths,
  })

  get propertyId() {
    return this.propertyIdParam.get()
  }

  get bookingId() {
    return this.bookingIdParam.get()
  }

  get blockId() {
    return this.blockIdParam.get()
  }

  get regions() {
    return this.computedRouteParams[HomeFilterIds.Regions].get()
  }

  get nightlyRates() {
    return this.computedRouteParams[ViewStateFilterIds.NightlyRates].get()
  }

  get reservationStatus() {
    return this.computedRouteParams[ViewStateFilterIds.ReservationStatus].get()
  }

  get paymentStatus() {
    return this.computedRouteParams[ViewStateFilterIds.PaymentStatus].get()
  }

  get depositOnHold() {
    return this.computedRouteParams[ViewStateFilterIds.DepositsOnHold].get() === 'true'
  }

  get bookingIssues() {
    return this.computedRouteParams[ViewStateFilterIds.BookingIssues].get()
  }

  get colorMode() {
    return this.computedRouteParams[ViewStateFilterIds.ColorBy].get()
  }

  get operationReadinessStatus() {
    return this.computedRouteParams[HomeFilterIds.OperationReadinessStatus].get()
  }

  get arrivalsMode(): boolean | undefined {
    return this.computedRouteParams[ViewStateFilterIds.ArrivalsMode].get()
  }

  get checkInDates() {
    const dateRange: AvailableDatesRange | undefined =
      this.computedRouteParams[HomeFilterIds.CheckInDates].get()
    if (!dateRange) {
      return undefined
    }

    const start = parseISODate(dateRange.checkIn)
    const end = parseISODate(dateRange.checkOut)
    return { start, end }
  }

  get availableDates() {
    const dateRange: AvailableDatesRange | undefined =
      this.computedRouteParams[CustomFilterIds.AvailableDates].get()
    if (!dateRange) {
      return undefined
    }
    const start = parseISODate(dateRange.checkIn)
    const end = parseISODate(dateRange.checkOut)
    return { start, end }
  }

  setViewFrom(date: Date) {
    this.computedRouteParams[ViewStateFilterIds.ViewFrom].replace(formatDate(date, ISO_DATE_FORMAT))
  }

  setColorMode(mode: BookingsColorMode) {
    this.computedRouteParams[ViewStateFilterIds.ColorBy].push(mode)
  }

  setAddressFilter(value?: string) {
    this.computedRouteParams[HomeFilterIds.AddressRadius].push(value)
  }

  setOperationReadinessStatus(status?: HomeOperationsReadinessStatus) {
    this.computedRouteParams[HomeFilterIds.OperationReadinessStatus].replace(status)
  }

  setCheckInDates(checkInDates?: AvailableDatesRange) {
    this.computedRouteParams[HomeFilterIds.CheckInDates].replace(checkInDates)
  }

  clearAllFilters() {
    this.activeFilters
      .filter(filter => filter.id !== ViewStateFilterIds.ArrivalsMode)
      .forEach(({ id }) => this.clearFilter(id))
  }

  get isTapeChartActive() {
    return (
      this.areInitialFiltersApplied &&
      !!matchPath(RoutingStore.location.pathname, ROUTES.fastTapeChart)
    )
  }

  setInitialFiltersApplied(areApplied: boolean) {
    this.areInitialFiltersApplied = areApplied
  }

  applyDefaultFilters() {
    const homeFiltersIds: string[] = Object.values(HomeFilterIds)

    if (!this.activeFilters.some(filter => homeFiltersIds?.includes(filter.id))) {
      this.applyFilters({
        [HomeFilterIds.ListingStatus]: [
          ListingStatus.earlyListed,
          ListingStatus.preListed,
          ListingStatus.listed,
        ],
        [HomeFilterIds.GuestReadinessStatus]: [
          GuestReadinessStatus.offboarding,
          GuestReadinessStatus.onboarding,
          GuestReadinessStatus.onboarded,
        ],
        [HomeFilterIds.AccountStatus]: [AccountStatus.active],
      })
    }
  }

  toggleArrivalMode(value?: boolean) {
    this.computedRouteParams[ViewStateFilterIds.ArrivalsMode].push(value)

    const shouldResetCheckInDates =
      this.checkInDates?.start &&
      this.checkInDates?.end &&
      formatDate(this.checkInDates.start) === formatDate(new Date()) &&
      formatDate(this.checkInDates.end) === formatDate(addDays(new Date(), 1))

    if (!value && shouldResetCheckInDates) {
      this.computedRouteParams[HomeFilterIds.CheckInDates].replace(undefined)
    }

    if (!this.arrivalsMode && !!tapechartGridSizingStore.isGridMaximised) {
      tapechartGridSizingStore.setGridSizing(false)
    }
  }

  setBlockId(blockId?: string) {
    computedRouteParam<string | undefined>('block-id', {
      patterns: [ROUTES.fastTapeChart.path],
    }).push(blockId)
  }

  setBookingId(bookingId?: string) {
    computedRouteParam<string | undefined>('booking-id', {
      patterns: [ROUTES.fastTapeChart.path],
    }).push(bookingId)
  }
}

export const fastBookingsTapeChartFiltersStore = new FastBookingsTapeChartFiltersStore()
